import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BibliographyEntry, ResourceClass } from '../types/atlas';

interface ReferenceTooltipProps {
  reference: string;
  entry: BibliographyEntry;
  pageRefs: string[];
  position: { x: number; y: number };
  resourceClass: ResourceClass;
  onOpenPdf: (pageRef?: string) => void;
  onClose: () => void;
}

export const ReferenceTooltip: React.FC<ReferenceTooltipProps> = ({
  reference,
  entry,
  pageRefs,
  position,
  resourceClass,
  onOpenPdf,
  onClose
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const sortPageRefs = () => {
    if (!pageRefs.length) return [];

    return [...pageRefs].sort((a, b) => {
      const labelA = entry.details?.[a]?.label || '';
      const labelB = entry.details?.[b]?.label || '';

      const numA = parseInt(labelA, 10);
      const numB = parseInt(labelB, 10);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return numA - numB;
      }
      return labelA.localeCompare(labelB);
    });
  };

  // Adjust position to ensure tooltip stays within viewport
  const adjustedPosition = () => {
    if (!tooltipRef.current) return position;
    
    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let { x, y } = position;
    
    // Adjust horizontal position if needed
    if (x + rect.width > viewportWidth - 20) {
      x = Math.max(20, viewportWidth - rect.width - 20);
    }
    
    // Adjust vertical position if needed
    if (y + rect.height > viewportHeight - 20) {
      y = Math.max(20, viewportHeight - rect.height - 20);
    }

    // Also check if tooltip is off the left or top edge
    if (x < 20) x = 20;
    if (y < 20) y = 20;
    
    return { x, y };
  };

  // Add click outside handler and mouse monitoring
  useEffect(() => {
    // Handle clicks outside the tooltip
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Track mouse position to prevent tooltip from closing when moving to it
    const handleMouseMove = (event: MouseEvent) => {
      if (!tooltipRef.current) return;
      
      const tooltip = tooltipRef.current;
      const rect = tooltip.getBoundingClientRect();
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      
      // Check if mouse is within the tooltip or is moving toward the tooltip
      // This prevents the tooltip from disappearing when mouse moves toward it
      const isWithinTooltip = 
        mouseX >= rect.left - 5 && 
        mouseX <= rect.right + 5 && 
        mouseY >= rect.top - 5 && 
        mouseY <= rect.bottom + 5;
      
      // Store this state on the ref so it's accessible from outside this handler
      (tooltip as any).isMouseWithin = isWithinTooltip;
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [onClose]);

  const pos = adjustedPosition();
  const sortedPageRefs = sortPageRefs();
  const hasPageRefs = sortedPageRefs.length > 0;

  const tooltipContent = (
    <div
      className="reference-tooltip"
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: `${pos.y}px`,
        left: `${pos.x}px`,
        width: "400px",
        boxSizing: "border-box",
        zIndex: 9999 // Ensure it's above everything else
      }}
      onMouseEnter={() => {
        // Mark as mouse being over the tooltip
        if (tooltipRef.current) {
          (tooltipRef.current as any).isMouseWithin = true;
        }
      }}
      onMouseLeave={() => {
        // Mark as mouse leaving the tooltip
        if (tooltipRef.current) {
          (tooltipRef.current as any).isMouseWithin = false;
          // Close after a small delay to ensure it's really leaving
          setTimeout(() => {
            if (tooltipRef.current && !(tooltipRef.current as any).isMouseWithin) {
              onClose();
            }
          }, 50);
        }
      }}
    >
      <div className="reference-tooltip-header">
        <span className="reference-tooltip-title">{entry.title}</span>
        <button
          className="reference-tooltip-close"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="reference-tooltip-date">
        <span className={`reference-tooltip-class reference-tooltip-class-${resourceClass.toLowerCase()}`}>
          {resourceClass}
        </span>
        {entry.source && <><span className="reference-tooltip-source">{entry.source}</span><span className="date-separator">|</span></>}
        {entry.date}
      </div>
      
      <div className="reference-tooltip-description">
        {entry.description}
      </div>
      
      {entry.comment && (
        <div className="reference-tooltip-comment">
          {entry.comment}
        </div>
      )}
      
      <div className="reference-tooltip-actions">
        {hasPageRefs ? (
          <div>
            {sortedPageRefs.map((pageKey) => {
              const pageDetail = entry.details?.[pageKey];
              const isPageMissing = !pageDetail;
              const pageLabel = pageDetail?.label || pageKey;

              return (
                <button
                  key={`page-btn-${pageKey}`}
                  className={`reference-tooltip-action ${isPageMissing ? 'reference-tooltip-action-missing' : ''}`}
                  onClick={isPageMissing ? undefined : () => onOpenPdf(pageKey)}
                  style={{
                    marginRight: '8px',
                    marginBottom: '8px',
                    ...(isPageMissing ? {
                      opacity: 0.6,
                      cursor: 'not-allowed',
                      backgroundColor: 'var(--error-bg-color, #fee)',
                      color: 'var(--error-color, #c00)',
                      borderColor: 'var(--error-color, #c00)'
                    } : {})
                  }}
                  title={isPageMissing ? `Page reference ${pageKey} not found in this document` : undefined}
                >
                  {/^\d+$/.test(pageLabel) ? `Page ${pageLabel}` : pageLabel}{isPageMissing ? ' ⚠' : ''}
                </button>
              );
            })}
          </div>
        ) : (
          <button
            className="reference-tooltip-action"
            onClick={() => onOpenPdf()}
          >
            View Document
          </button>
        )}
      </div>
    </div>
  );

  // Render the tooltip using a portal to document.body to avoid positioning context issues
  return createPortal(tooltipContent, document.body);
};
